import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { useAtomValue, useAtom } from 'jotai';
import northPolygonData from '../../Components/Map/Geojsons/northPolygonData.geojson';
import centralAPolygonData from '../../Components/Map/Geojsons/centralAPolygonData.geojson';
import centralBPolygonData from '../../Components/Map/Geojsons/centralBPolygonData.geojson';
import kashmirPolygonData from '../../Components/Map/Geojsons/kashmirPolygonData.geojson';
import southPolygonData from '../../Components/Map/Geojsons/southPolygonData.geojson';
import completePakistanPolygonData from '../../Components/Map/Geojsons/completePakistanPolygonData.geojson';
import pakistanPolygonData from '../../Components/Map/Geojsons/pakistanPolygonData.geojson';
import MapIcon from '@mui/icons-material/Map';
import TableChartIcon from '@mui/icons-material/TableChart';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import Tooltip from '@mui/material/Tooltip';
import { Switch, FormControlLabel, FormGroup } from '@mui/material';

import { fetchNetworkLayerData } from './functions/get_NetworkLayer.functions';
import { fetchInventoryDataByNetwork } from './functions/get_inventory.functions';
import { fetchFiberSiteData } from './functions/get_FiberSites.functions';
import { geoServerLinks } from '../../common/functions/get_geoServer_links.function';

import ViewPopup from './ViewPopup';
import NetworkLayerTable from './networkLayer_table.component';

import {
  networkVisulization,
  selectedDisplayedKmz,
  noNetwork,
  showRanSitesAtom,
  showFiberSitesAtom,
  showFiberCablesAtom,
} from '../../common/atom/atoms';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { boundaryLayer } from '../../common/functions/pak_boundry.function';
import { removeWmsLayer } from '../../common/functions/remove_wms_layer.function';
import { removeWmsSitesLayer } from '../../common/functions/remove_wms_site_layers.function';
import { handleRefresh } from '../../common/functions/refresh_map.function';
import { addWmsLayer } from './functions/add_wms_layer.function';
import RefreshMap from '../../common/components/refresh_map_component';

import expandButton from '../../Assets/AlertIcon/filter.svg';
import crossIcon from '../../Assets/AlertIcon/crossIcon.svg';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
mapboxgl.accessToken = process.env.REACT_APP_PUBLIC_MAP_BOX_GL_ACCESS_TOKEN;

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const vendorColorMap = {
  jazz: '#EF4444',
  wateen: '#2563EB',
  mppl: '#8B5CF6',
  ptcl: '#10B981',
  wtl: '#F97316',
  nayatel: '#CA8A04',
};

const normalizeInventoryItem = (item) => {
  const fiberPair = parseNumber(item.fiber_pair ?? item.pair_count);
  const usedPair = parseNumber(item.used_pair);
  const availablePairExplicit = parseNumber(item.available_pair ?? item.unused_pair);
  const computedAvailablePair =
    fiberPair !== undefined && usedPair !== undefined
      ? Math.max(fiberPair - usedPair, 0)
      : undefined;
  const commissionDate = item.commission_date ?? item.commissioned_at ?? null;
  const tenancyLabel = item.fiber_tenancy_type ?? item.tenancy_type ?? '';
  const normalizedTenancy = tenancyLabel ? String(tenancyLabel).toLowerCase().trim() : '';
  const isRowValue = item.is_row;
  const normalizedIsRow =
    typeof isRowValue === 'boolean'
      ? isRowValue
      : normalizedTenancy === 'row' ||
        (isRowValue !== undefined &&
          ['true', '1'].includes(String(isRowValue).toLowerCase().trim()));

  return {
    ...item,
    region: item.region ?? item.source_region ?? '',
    city: item.city ?? item.source_city ?? '',
    ring_unique_name: item.ring_unique_name ?? item.source_ring ?? '',
    source_region: item.source_region ?? item.region ?? '',
    source_city: item.source_city ?? item.city ?? '',
    source_ring: item.source_ring ?? item.ring_unique_name ?? '',
    trail_name: item.trail_name ?? '',
    fiber_pair: fiberPair ?? item.fiber_pair ?? item.pair_count ?? null,
    pair_count: fiberPair ?? item.pair_count ?? item.fiber_pair ?? null,
    used_pair: usedPair ?? item.used_pair ?? null,
    unused_pair: availablePairExplicit ?? computedAvailablePair ?? null,
    available_pair: computedAvailablePair ?? availablePairExplicit ?? null,
    commission_date: commissionDate,
    commissioned_at: commissionDate,
    link_rate_gbps: item.link_rate_gbps ?? item.link_rate ?? '',
    fiber_tenancy_type: tenancyLabel,
    tenancy_type: tenancyLabel,
    tenancy_type_normalized: normalizedTenancy,
    is_row: normalizedIsRow,
    scope_year: item.scope_year ?? item.scopeYear ?? '',
    lease_start_date: item.lease_start_date ?? null,
    lease_end_date: item.lease_end_date ?? null,
    concern_department: item.concern_department ?? 'N/A',
    poc: item.poc ?? 'N/A',
    poc_phone: item.poc_phone ?? 'N/A',
    vendor: item.vendor ?? '',
  };
};

const NetworkLayerMapComponent = () => {
  const mapContainer = useRef(null);
  const controllerRef = useRef(null);
  const markerRef = useRef(null);
  const navigate = useNavigate();
  let currentPopup = null;
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const showBackdropRef = useRef(showBackdrop);
  const hideBackdropRef = useRef(hideBackdrop);
  const map = useRef(null);

  // Update refs when backdrop functions change
  useEffect(() => {
    showBackdropRef.current = showBackdrop;
    hideBackdropRef.current = hideBackdrop;
  }, [showBackdrop, hideBackdrop]);
  const noNetworks = useAtomValue(noNetwork);
  const currentKmzNetwork = useAtomValue(networkVisulization);
  const [viewType, setViewType] = useState('graph');
  const [state, setState] = useState({ right: false });
  const [ids, setIDs] = useAtom(selectedDisplayedKmz);
  const [lng, setLng] = useState(69.3451);
  const [lat, setLat] = useState(30.3753);
  const stepRef = useRef(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const seeButtonRef = useRef(null);
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [regionList, setRegionList] = useState([]);
  const [networkLayerData, setNetworkLayerData] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [technologiesList, setTechnologiesList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [siteStatusList, setSiteStatusList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [nodeNameList, setNodeNameList] = useState([]);
  const [fiberPopList, setFiberPopList] = useState([]);
  const [siteCodeList, setSiteCodeList] = useState([]);
  const [showColorCoding, setShowColorCoding] = useState(false);
  const showColorCodingRef = useRef(false);
  const [inventoryData, setInventoryData] = useState([]);
  const inventoryDataRef = useRef([]);
  const [fiberFilters, setFiberFilters] = useState({
    region: '',
    nodeName: '',
    city: '',
    vendor: '',
    fiberPop: '',
    status: '',
  });

  const [nodeFilters, setNodeFilters] = useState({
    siteCode: '',
    technology: '',
    siteStatus: '',
  });
  const [showRanSites, setShowRanSites] = useAtom(showRanSitesAtom);
  const [showFiberSites, setShowFiberSites] = useAtom(showFiberSitesAtom);
  const [showFiberCables, setShowFiberCables] = useAtom(showFiberCablesAtom);
  const [fiberSiteData, setFiberSiteData] = useState([]);
  const [fiberCableData, setFiberCableData] = useState([]);
  const [linksData, setLinksData] = useState(null);
  const linksDataFetchedRef = useRef({});
  const linksDataNetworkRef = useRef(null); // Track which network the current linksData belongs to
  const [legendTop, setLegendTop] = useState(70);

  // Track loading state for all APIs
  const apiLoadingRef = useRef({
    ranSite: false,
    fiberSite: false,
    links: false,
    inventory: false,
  });

  // Helper to check if all APIs are done loading
  const checkAllApisComplete = useCallback(() => {
    const loading = apiLoadingRef.current;
    const allComplete =
      !loading.ranSite && !loading.fiberSite && !loading.links && !loading.inventory;

    // Only hide loader if all APIs are complete
    if (allComplete) {
      hideBackdropRef.current();
    }
  }, []);

  // Helper to mark API as loading
  const markApiLoading = useCallback((apiName, isLoading) => {
    const previousState = apiLoadingRef.current[apiName];
    apiLoadingRef.current[apiName] = isLoading;

    // Show loader when any API starts loading
    if (isLoading && !previousState) {
      showBackdropRef.current();
    }

    // Only hide loader when ranSite (fetchNetworkLayerData) completes
    // Other APIs can complete, but loader stays visible until ranSite is done
    if (!isLoading && previousState && apiName === 'ranSite') {
      hideBackdropRef.current();
    }
  }, []);

  const FiberDropdownConfig = [
    { key: 'city', label: 'City', options: cityList },
    { key: 'nodeName', label: 'Node Name', options: nodeNameList },
    { key: 'fiberPop', label: 'Fiber POP', options: fiberPopList },
    { key: 'status', label: 'Status', options: statusList },
    { key: 'vendor', label: 'Vendor', options: vendorList },
  ];

  const NodeDropdownConfig = [
    { key: 'siteCode', label: 'Site Code', options: siteCodeList },
    { key: 'technology', label: 'Technology', options: technologiesList },
    { key: 'siteStatus', label: 'Site Status', options: siteStatusList },
  ];

  const vendorColors = {
    Huawei: '#CE2105',
    ZTE: '#4CAF50',
    Ericsson: '#3F51B5',
    default: '#01B7FE',
  };

  const flyToLocation = (map, coordinates, zoom = 10) => {
    if (!map?.flyTo || !coordinates) {
      return;
    }

    // Validate coordinates are valid numbers (not NaN, null, or undefined)
    const [lng, lat] = coordinates;
    if (
      typeof lng !== 'number' ||
      typeof lat !== 'number' ||
      isNaN(lng) ||
      isNaN(lat) ||
      !isFinite(lng) ||
      !isFinite(lat)
    ) {
      console.warn('Invalid coordinates provided to flyToLocation:', coordinates);
      return;
    }

    map.flyTo({
      center: coordinates,
      zoom,
      essential: true,
    });
  };

  const [regions, setRegions] = useState([
    {
      id: 'north-polygon',
      regionName: 'North',
      data: northPolygonData,
      color: '#c5d5ef',
      center: [72.94996, 34.97499],
    },
    {
      id: 'centralA-polygon',
      regionName: 'Central A',
      data: centralAPolygonData,
      color: '#f1bdb6',
      center: [73.40944, 30.59002],
    },
    {
      id: 'centralB-polygon',
      regionName: 'Central B',
      data: centralBPolygonData,
      color: '#c4d8a5',
      center: [71.16216, 30.48678],
    },
    {
      id: 'south-polygon',
      regionName: 'South',
      data: southPolygonData,
      color: '#f8e1b9',
      center: [66.5, 27.48678],
    },
    {
      id: 'kashmir-polygon',
      regionName: 'Kashmir',
      data: kashmirPolygonData,
      color: '#f2c3fa',
      center: [73.781, 33.9259],
    },
    {
      id: 'pakistan',
      regionName: 'Pakistan',
      data: completePakistanPolygonData,
      color: '#5f8282',
      center: [72.94996, 34.97499],
    },
  ]);

  const [regionsMpbn, setRegionsMpbn] = useState([
    {
      id: 'north-polygon',
      regionName: 'North',
      alarmsCount: 0,
      color: '#35527e',
      center: [72.94996378341335, 34.974994348955335],
    },
    {
      id: 'centralA-polygon',
      regionName: 'Center A',
      alarmsCount: 0,
      color: '#893e2a',
      center: [73.40944846601698, 30.590024194213726],
    },
    {
      id: 'centralB-polygon',
      regionName: 'Center B',
      alarmsCount: 0,
      color: '#66773c',
      center: [71.16216343821162, 30.486783539931153],
    },
    {
      id: 'south-polygon',
      regionName: 'South',
      alarmsCount: 0,
      color: '#906b23',
      center: [66.5, 27.486783539931153],
    },
    {
      id: 'pakistan',
      regionName: 'Pakistan',
      alarmsCount: 0,
      data: pakistanPolygonData,
      color: '#446363',
      center: [72.94996378341335, 34.974994348955335],
    },
    {
      id: 'kashmir-polygon',
      regionName: 'Kashmir',
      alarmsCount: 0,
      data: kashmirPolygonData,
      color: '#446363',
      center: [73.781, 33.9259],
    },
  ]);

  const fetchData = async () => {
    try {
      // Don't mark as loading here - it's already marked in useEffect
      // markApiLoading('ranSite', true);
      const result = await fetchNetworkLayerData();

      // Ensure API response is received and has data before proceeding
      if (!result || !result.response) {
        console.error('Invalid API response received for fetchNetworkLayerData');
        markApiLoading('ranSite', false);
        return;
      }

      const actualData = result.response;

      // Ensure actualData is an array before processing
      if (!Array.isArray(actualData)) {
        console.error('API response data is not an array');
        markApiLoading('ranSite', false);
        return;
      }

      const uniqueSiteStatuses = [
        ...new Set(actualData.map((item) => item.site_status).filter(Boolean)),
      ];
      const uniqueTechnologies = [
        ...new Set(actualData.map((item) => item.physical).filter(Boolean)),
      ];
      const uniqueSiteCode = [...new Set(actualData.map((item) => item.site_code).filter(Boolean))];

      setNetworkLayerData(actualData);

      setSiteStatusList(uniqueSiteStatuses);
      setTechnologiesList(uniqueTechnologies);
      setSiteCodeList(uniqueSiteCode);

      const geoJsonFromApiData = {
        type: 'FeatureCollection',
        features: actualData
          .filter((item) => item.lat && item.long)
          .map((item) => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [parseFloat(item.long), parseFloat(item.lat)],
            },
            properties: {
              site_code: item.site_code,
              vendor: item.vendor,
              technology: item.physical,
              site_status: item.site_status,
              vendor_color: vendorColors[item.vendor] || vendorColors.default,
            },
          })),
      };

      addWmsSitessLayer(geoJsonFromApiData);

      // Only mark as complete after API response is fully received and processed
      markApiLoading('ranSite', false);
    } catch (error) {
      console.error('Error fetching ran site data:', error);
      markApiLoading('ranSite', false);
    }
  };

  const fetchFiberData = async () => {
    try {
      // Don't mark as loading here - it's already marked in useEffect
      // markApiLoading('fiberSite', true);
      const result = await fetchFiberSiteData();
      const actualData = result.response;

      setFiberSiteData(actualData);

      const uniqueRegions = [...new Set(actualData.map((item) => item.region).filter(Boolean))];

      const uniqueVendors = [...new Set(actualData.map((item) => item.bp).filter(Boolean))];
      const uniqueNodeNames = [...new Set(actualData.map((item) => item.site_id).filter(Boolean))];
      const uniqueFiberCities = [...new Set(actualData.map((item) => item.city).filter(Boolean))];
      const uniqueFiberPops = [
        ...new Set(actualData.map((item) => item.fiber_pop_id).filter(Boolean)),
      ];
      const uniqueFiberStatuses = [
        ...new Set(actualData.map((item) => item.status).filter(Boolean)),
      ];

      setRegionList(uniqueRegions);
      setNodeNameList(uniqueNodeNames);
      setCityList(uniqueFiberCities);
      setVendorList(uniqueVendors);
      setFiberPopList(uniqueFiberPops);
      setStatusList(uniqueFiberStatuses);

      const fiberSitesGeoJson = {
        type: 'FeatureCollection',
        features: actualData
          .filter((item) => item.lat && item.long)
          .map((item) => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [parseFloat(item.long), parseFloat(item.lat)],
            },
            properties: {
              site_code: item.id,
              region: item.region,
              city: item.city,
              status: item.status,
            },
          })),
      };

      await addWMSFiberSitesLayer(fiberSitesGeoJson);
      markApiLoading('fiberSite', false);
    } catch (error) {
      console.error('Error fetching fiber data:', error);
      markApiLoading('fiberSite', false);
    }
  };

  const getFilteredFiberSites = () => {
    return fiberSiteData.filter((site) => {
      return (
        (!fiberFilters.region || site.region === fiberFilters.region) &&
        (!fiberFilters.city || site.city === fiberFilters.city) &&
        (!fiberFilters.nodeName || site.site_id === fiberFilters.nodeName) &&
        (!fiberFilters.vendor || site.bp === fiberFilters.vendor) &&
        (!fiberFilters.fiberPop || site.fiber_pop_id === fiberFilters.fiberPop) &&
        (!fiberFilters.status || site.status === fiberFilters.status)
      );
    });
  };

  const getFilteredRanSites = () => {
    return networkLayerData.filter((site) => {
      return (
        (!nodeFilters.siteCode || site.site_code === nodeFilters.siteCode) &&
        (!nodeFilters.technology || site.physical === nodeFilters.technology) &&
        (!nodeFilters.siteStatus || site.site_status === nodeFilters.siteStatus)
      );
    });
  };

  // Filter links data based on fiberFilters
  // Uses inventory data as bridge: filter inventory by fiberFilters, then match links by layer_name
  const getFilteredLinksData = useCallback(() => {
    if (!linksData || !linksData.features) {
      return linksData;
    }

    // If no filters are applied, return all links
    const hasFilters = Object.values(fiberFilters).some((filter) => filter !== '');
    if (!hasFilters) {
      return linksData;
    }

    // Helper to normalize region names for comparison (handles "Central A" vs "Center A", etc.)
    const normalizeRegionName = (name) => {
      if (!name) {
        return '';
      }
      let normalized = String(name).trim().toLowerCase();
      // Remove all spaces
      normalized = normalized.replace(/\s+/g, '');
      // Normalize "Center A" to "Central A" (both become "centrala")
      if (normalized === 'centera') {
        normalized = 'centrala';
      }
      if (normalized === 'centerb') {
        normalized = 'centralb';
      }
      return normalized;
    };

    // Filter inventory data based on fiberFilters (inventory has region, city, vendor, etc.)
    // Note: inventory might not have all properties like fiber_pop_id or status
    const filteredInventory = (inventoryDataRef.current || []).filter((item) => {
      const filterRegionNormalized = normalizeRegionName(fiberFilters.region);
      const itemRegionNormalized = normalizeRegionName(item.region);
      const itemSourceRegionNormalized = normalizeRegionName(item.source_region);

      const matchesRegion =
        !fiberFilters.region ||
        itemRegionNormalized === filterRegionNormalized ||
        itemSourceRegionNormalized === filterRegionNormalized;
      const matchesCity =
        !fiberFilters.city ||
        item.city === fiberFilters.city ||
        item.source_city === fiberFilters.city;
      const matchesNodeName =
        !fiberFilters.nodeName ||
        item.source_site_code === fiberFilters.nodeName ||
        item.sink_site_code === fiberFilters.nodeName;
      const matchesVendor = !fiberFilters.vendor || item.vendor === fiberFilters.vendor;
      // Only filter by fiber_pop_id and status if the filter is set AND inventory has the property
      // If property doesn't exist in inventory, skip that filter (treat as matching)
      const matchesFiberPop =
        !fiberFilters.fiberPop ||
        item.fiber_pop_id === undefined ||
        item.fiber_pop_id === fiberFilters.fiberPop;
      const matchesStatus =
        !fiberFilters.status || item.status === undefined || item.status === fiberFilters.status;

      return (
        matchesRegion &&
        matchesCity &&
        matchesNodeName &&
        matchesVendor &&
        matchesFiberPop &&
        matchesStatus
      );
    });

    // Get layer names from filtered inventory (inventory has layer_name that matches links)
    // Normalize layer names for case-insensitive matching (same as inventory matching logic)
    const getLayerKey = (value) => (value ? String(value).trim().toLowerCase() : '');
    const filteredLayerNames = new Set(
      filteredInventory.map((item) => getLayerKey(item.layer_name)).filter((key) => key !== '')
    );

    // If no matching inventory items, return empty (no links to show)
    if (filteredLayerNames.size === 0) {
      console.log('No matching inventory items found, returning empty links');
      return {
        ...linksData,
        features: [],
      };
    }

    // Filter links to only show those matching filtered inventory layer names
    const filteredFeatures = linksData.features.filter((feature) => {
      const layerName =
        feature.properties?.layer_name ??
        feature.properties?.layerName ??
        feature.properties?.trail_name ??
        feature.properties?.trailName;

      if (!layerName) {
        return false; // Don't show links without layer names when filters are applied
      }

      // Normalize layer name for comparison (case-insensitive, trimmed)
      const normalizedLayerName = getLayerKey(layerName);

      // Check if this link's layer name matches any filtered inventory's layer name
      const matches = filteredLayerNames.has(normalizedLayerName);
      return matches;
    });

    return {
      ...linksData,
      features: filteredFeatures,
    };
  }, [linksData, fiberFilters, inventoryData]);

  const fetchLinksData = async () => {
    if (!currentKmzNetwork) {
      return;
    }

    // Store the network we're fetching for to verify later
    const networkBeingFetched = currentKmzNetwork;

    // Check if we've already fetched for this network
    if (linksDataFetchedRef.current[networkBeingFetched]) {
      // If we already have data cached, we need to fetch it again or use cached data
      // For now, let's fetch fresh data to ensure we have the latest
      // Or we could use cached data, but that requires storing it somewhere
      // For simplicity, let's just fetch again
    }

    try {
      // Don't mark as loading here - it's already marked in useEffect
      // markApiLoading('links', true);
      const controller = new AbortController();
      const data = await geoServerLinks(networkBeingFetched, controller.signal);

      // Only set data if we're still on the same network (prevent race conditions)
      if (data && data.features && data.features.length > 0) {
        // Double-check we're still on the same network before setting data
        if (currentKmzNetwork === networkBeingFetched) {
          setLinksData(data);
          linksDataNetworkRef.current = networkBeingFetched; // Track which network this data belongs to
          linksDataFetchedRef.current[networkBeingFetched] = true;
        }
      }
      markApiLoading('links', false);
    } catch (error) {
      console.error('Error fetching links data:', error);
      markApiLoading('links', false);
    }
  };

  useEffect(() => {
    // Only fetch APIs when network changes, not when toggles change
    // Mark APIs as loading BEFORE calling them to prevent race conditions
    apiLoadingRef.current = {
      ranSite: true, // Always fetch RAN Site data when network changes
      fiberSite: true, // Always fetch Fiber Site data when network changes
      links: !!currentKmzNetwork, // Mark as loading if we're going to fetch
      inventory: !!currentKmzNetwork, // Will be set by inventory fetch useEffect
    };

    // Show loader immediately if network exists
    if (currentKmzNetwork) {
      showBackdropRef.current();
    }

    // Always fetch all APIs when network changes
    if (currentKmzNetwork) {
      fetchData(); // fetchData will mark ranSite as false when complete
      fetchFiberData(); // fetchFiberData will mark fiberSite as false when complete
      fetchLinksData(); // fetchLinksData will mark links as false when complete
    }

    // Inventory is fetched in a separate useEffect, so it will handle its own loading state
    // Don't call checkAllApisComplete here - let each API mark itself as complete when done
  }, [currentKmzNetwork]);

  // useEffect(() => {
  //   const abortController = new AbortController();
  //   const handleData = async () => {
  //     try {
  //       if (!map.current || !map.current.isStyleLoaded()) {
  //         map.current.once('style.load', async () => {
  //           if (showRanSites) {
  //             await fetchData(abortController.signal);
  //           }
  //           if (showFiberSites) {
  //             await fetchFiberData(abortController.signal);
  //           }
  //         });
  //       } else {
  //         if (showRanSites) {
  //           await fetchData(abortController.signal);
  //         }
  //         if (showFiberSites) {
  //           await fetchFiberData(abortController.signal);
  //         }
  //       }
  //     } catch (err) {
  //       if (err.name !== 'AbortError') {
  //         console.error('Layer fetch error', err);
  //       }
  //     }
  //   };

  //   handleData();
  //   return () => {
  //     abortController.abort(); // Cancel previous fetch
  //   };
  // }, [currentKmzNetwork]);

  // Update ref when showColorCoding changes
  useEffect(() => {
    showColorCodingRef.current = showColorCoding;
  }, [showColorCoding]);

  // Create/update links layer when linksData is available
  // Recreate layer when showColorCoding or inventoryData changes to ensure colors are applied
  const linksLayerCreatedForNetworkRef = useRef(null);
  const lastShowColorCodingRef = useRef(showColorCoding);
  const lastInventoryLengthRef = useRef(0);

  // Clear links layer and data immediately when network changes
  useEffect(() => {
    if (!map.current) {
      return;
    }

    // Always clear layer and data when network changes (even on first load)
    const previousNetwork = linksLayerCreatedForNetworkRef.current;

    // Remove layer from map immediately
    if (map.current.getSource('linksLayers')) {
      removeWmsLayer(map);
    }

    // Clear linksData state to prevent using old network's data
    setLinksData(null);
    linksDataNetworkRef.current = null; // Clear the network tracking ref

    // Reset tracking refs
    if (previousNetwork !== currentKmzNetwork) {
      linksLayerCreatedForNetworkRef.current = null;
      lastShowColorCodingRef.current = showColorCoding;
      lastInventoryLengthRef.current = 0;
    }
  }, [currentKmzNetwork]);

  useEffect(() => {
    if (!map.current || !linksData) {
      return;
    }

    // CRITICAL: Verify that linksData is for the current network
    // Don't create layer if data doesn't match current network
    if (linksDataNetworkRef.current !== currentKmzNetwork) {
      console.log(
        `Skipping layer creation: linksData is for ${linksDataNetworkRef.current}, but current network is ${currentKmzNetwork}`
      );
      return;
    }

    // Verify that linksData is for the current network
    // Don't create layer if data doesn't match current network
    // Recreate layer when filters change, color coding changes, or inventory changes
    // Since fiberFilters is in dependencies, this will run when filters change

    // Create/update layer with fetched data (no API call)
    const createLinksLayer = async () => {
      try {
        // Remove existing layer if it exists
        if (map.current.getSource('linksLayers')) {
          removeWmsLayer(map);
        }

        // Get filtered links data based on fiberFilters
        const filteredLinksData = getFilteredLinksData();

        // Pass no-op function for hideBackdrop to prevent hiding loader
        // Loader should only be hidden when fetchNetworkLayerData completes
        await addWmsLayer(
          map,
          currentKmzNetwork,
          currentPopup,
          stepRef,
          showBackdropRef.current,
          () => {}, // No-op function - don't hide loader from links layer
          null, // No controller needed since data is already fetched
          inventoryDataRef.current, // Pass inventory data
          showColorCoding, // Pass current showColorCoding state
          filteredLinksData // Pass filtered links data
        );

        linksLayerCreatedForNetworkRef.current = currentKmzNetwork;
        lastShowColorCodingRef.current = showColorCoding;
        lastInventoryLengthRef.current = inventoryData.length;

        // Set visibility based on showFiberCables after layer is created
        if (map.current.getLayer('linksLayers')) {
          map.current.setLayoutProperty(
            'linksLayers',
            'visibility',
            showFiberCables ? 'visible' : 'none'
          );
        }
        if (map.current.getLayer('linksLayers-dashed')) {
          map.current.setLayoutProperty(
            'linksLayers-dashed',
            'visibility',
            showFiberCables ? 'visible' : 'none'
          );
        }
      } catch (error) {
        console.error('Error creating links layer:', error);
      }
    };

    createLinksLayer();
  }, [
    linksData,
    currentKmzNetwork,
    showColorCoding,
    inventoryData.length,
    fiberFilters,
    getFilteredLinksData,
  ]);

  useEffect(() => {
    if (!map.current) {
      return;
    }

    // Only toggle visibility - no API calls
    if (map.current.getLayer('linksLayers')) {
      map.current.setLayoutProperty(
        'linksLayers',
        'visibility',
        showFiberCables ? 'visible' : 'none'
      );
    }

    if (map.current.getLayer('linksLayers-dashed')) {
      map.current.setLayoutProperty(
        'linksLayers-dashed',
        'visibility',
        showFiberCables ? 'visible' : 'none'
      );
    }
  }, [showFiberCables]);

  const handleRanToggle = () => {
    setShowRanSites((prev) => !prev);

    if (event.target.checked) {
      addWmsSitessLayer(getFilteredGeoJson());
    } else {
      if (map.current.getLayer('ranSitesLayer')) {
        map.current.removeLayer('ranSitesLayer');
      }
      if (map.current.getSource('ranSitesLayer')) {
        map.current.removeSource('ranSitesLayer');
      }
    }
  };

  const handleFiberToggle = (event) => {
    setShowFiberSites((prev) => !prev);

    if (event.target.checked) {
      addWMSFiberSitesLayer(getFiberSiteFilteredGeoJson());
    } else {
      if (map.current.getLayer('fiberSitesLayer')) {
        map.current.removeLayer('fiberSitesLayer');
      }
      if (map.current.getSource('fiberSitesLayer')) {
        map.current.removeSource('fiberSitesLayer');
      }
    }
  };

  const handleFiberCableToggle = (event) => {
    const checked = event.target.checked;
    setShowFiberCables(checked);

    // if (map.current.getLayer('linksLayers')) {
    //   map.current.setLayoutProperty('linksLayers', 'visibility', checked ? 'visible' : 'none');
    // }

    // if (map.current.getLayer('linksLayers-dashed')) {
    //   map.current.setLayoutProperty(
    //     'linksLayers-dashed',
    //     'visibility',
    //     checked ? 'visible' : 'none'
    //   );
    // }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Always fetch inventory data when network changes, even if previously fetched
      // This ensures we always have the latest data for the current network
      try {
        markApiLoading('inventory', true);
        const result = await fetchInventoryDataByNetwork(currentKmzNetwork);
        const actualData = Array.isArray(result?.response) ? result.response : [];
        const normalizedData = actualData.map(normalizeInventoryItem);
        inventoryDataRef.current = normalizedData;
        setInventoryData(normalizedData);
        markApiLoading('inventory', false);
      } catch (error) {
        console.error('Error fetching inventory data:', error);
        markApiLoading('inventory', false);
      }
    };

    if (currentKmzNetwork) {
      fetchData();
    }
  }, [currentKmzNetwork]);

  useEffect(() => {
    let tab = currentKmzNetwork.toLowerCase();

    if (!map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/standard',
        center: [lng, lat],
        zoom: 2,
        attributionControl: false,
      });
      map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

      map.current.on('style.load', () => {
        console.log('Style fully loaded, adding boundary...');

        boundaryLayer(map.current, regions, regionsMpbn, tab);
      });
    } else {
      map.current.setCenter([lng, lat]);
    }
  }, []);

  // useEffect(() => {
  //   addLinksLayer();
  // }, [showColorCoding]);

  useEffect(() => {
    if (!map.current) {
      return;
    }

    // Update color when showColorCoding changes
    // Links should always be visible, only color changes
    if (map.current.getLayer('linksLayers')) {
      map.current.setPaintProperty(
        'linksLayers',
        'line-color',
        showColorCoding ? ['get', 'assignedColor'] : '#247d42'
      );
    }

    if (map.current.getLayer('linksLayers-dashed')) {
      map.current.setPaintProperty(
        'linksLayers-dashed',
        'line-color',
        showColorCoding ? ['get', 'assignedColor'] : '#247d42'
      );
    }
  }, [showColorCoding]);

  // Calculate legend position based on popup state
  useEffect(() => {
    const updateLegendPosition = () => {
      if (popupOpen && seeButtonRef.current) {
        // Find the parent container with position: relative (the Box at line 1638)
        let parentContainer = seeButtonRef.current.parentElement;
        while (
          parentContainer &&
          window.getComputedStyle(parentContainer).position !== 'relative'
        ) {
          parentContainer = parentContainer.parentElement;
        }

        if (parentContainer) {
          const buttonRect = seeButtonRef.current.getBoundingClientRect();
          const containerRect = parentContainer.getBoundingClientRect();

          // ViewPopup is positioned at: rect.bottom + window.scrollY + 8
          // We need to calculate where the popup bottom would be, then convert to container coordinates
          const buttonBottomInDocument = buttonRect.bottom + window.scrollY;
          const popupTopInDocument = buttonBottomInDocument + 8; // 8px gap from button
          const popupHeight = 200; // Estimated height of ViewPopup (icon section + form section)
          const popupBottomInDocument = popupTopInDocument + popupHeight;

          // Convert popup bottom position to container-relative coordinates
          const containerTopInDocument = containerRect.top + window.scrollY;
          const legendTopPosition = popupBottomInDocument - containerTopInDocument + 8; // 8px gap below popup

          setLegendTop(legendTopPosition);
        } else {
          // Fallback: use simple calculation
          const rect = seeButtonRef.current.getBoundingClientRect();
          setLegendTop(rect.height + 8 + 200 + 8); // button height + gap + popup height + gap
        }
      } else {
        // Position below icons when popup is closed
        setLegendTop(70);
      }
    };

    updateLegendPosition();

    // Update on window resize/scroll when popup is open
    if (popupOpen) {
      window.addEventListener('resize', updateLegendPosition);
      window.addEventListener('scroll', updateLegendPosition, true);
      return () => {
        window.removeEventListener('resize', updateLegendPosition);
        window.removeEventListener('scroll', updateLegendPosition, true);
      };
    }
  }, [popupOpen]);

  const addWmsSitessLayer = async (geoJsonFromApiData) => {
    try {
      if (map.current.getLayer('ranSitesLayer')) {
        map.current.removeLayer('ranSitesLayer');
      }
      if (map.current.getLayer('highlightedSites')) {
        map.current.removeLayer('highlightedSites');
      }
      if (map.current.getSource('ranSitesLayer')) {
        map.current.removeSource('ranSitesLayer');
      }

      if (map.current.hasImage('custom-tower')) {
        map.current.removeImage('custom-tower');
      }

      if (window.haloAnimationFrame) {
        cancelAnimationFrame(window.haloAnimationFrame);
      }

      map.current.addSource('ranSitesLayer', {
        type: 'geojson',
        data: geoJsonFromApiData,
      });

      const createSvgIcon = (color) => `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <mask id="mask0_8069_6081" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
          <path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white"/>
        </mask>
        <g mask="url(#mask0_8069_6081)">
          <path d="M5.3916 23.2915H18.6104" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11.999 6.83454L12.2344 6.90396L16.0791 23.2915H7.91895L11.7778 6.84382L11.999 6.83454Z" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11.999 6.83447C12.8262 6.83447 13.501 6.15975 13.501 5.3325C13.501 4.5053 12.8262 3.83058 11.999 3.83058C11.1718 3.83058 10.4971 4.5053 10.4971 5.3325C10.4971 6.15975 11.1718 6.83447 11.999 6.83447Z" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.56957 7.91162C8.0104 7.16809 7.70801 6.26293 7.70801 5.33256C7.70801 4.34097 8.05141 3.38003 8.67987 2.61301" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6.50538 9.79883C5.4792 8.53653 4.91895 6.95938 4.91895 5.33258C4.91895 3.63514 5.52879 1.99414 6.63743 0.708688" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M15.4304 7.91162C15.9896 7.16809 16.292 6.26293 16.292 5.33256C16.292 4.34097 15.9485 3.38003 15.3201 2.61301" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M17.4948 9.79883C18.521 8.53653 19.0813 6.95938 19.0813 5.33258C19.0813 3.63514 18.4714 1.99414 17.3628 0.708688" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.12817 22.4001L14.9266 18.3787" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.79443 15.324L14.9073 18.3723" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.79443 15.324L13.6762 13.0494" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      </svg>
    `;

      const defaultSvgDataURL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(createSvgIcon('#01B7FE'))}`;
      const defaultImg = new Image();
      defaultImg.src = `${defaultSvgDataURL}#${new Date().getTime()}`;

      defaultImg.onload = () => {
        if (!map.current.hasImage('custom-tower')) {
          map.current.addImage('custom-tower', defaultImg, { pixelRatio: 2 });
        }

        map.current.addLayer({
          id: 'ranSitesLayer',
          type: 'symbol',
          source: 'ranSitesLayer',
          layout: {
            'icon-image': 'custom-tower',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 1, 13, 2],
            'icon-allow-overlap': true,
          },
        });
      };

      let haloSize = 3;
      let opacity = 0.5;
      let expanding = true;
      let maxHaloSize = 6;
      let minHaloSize = 3;
      let maxOpacity = 0.7;
      let minOpacity = 0.3;

      const animateHalo = () => {
        if (expanding) {
          haloSize += 0.05;
          opacity += 0.02;
          if (haloSize >= maxHaloSize || opacity >= maxOpacity) {
            expanding = false;
          }
        } else {
          haloSize -= 0.05;
          opacity -= 0.02;
          if (haloSize <= minHaloSize || opacity <= minOpacity) {
            expanding = true;
          }
        }

        if (map.current.getLayer('highlightedSites')) {
          map.current.setPaintProperty('highlightedSites', 'circle-radius', haloSize);
          map.current.setPaintProperty('highlightedSites', 'circle-opacity', opacity);
        }

        requestAnimationFrame(animateHalo);
      };

      animateHalo();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error adding WFS layer:', error);
      }
    }
  };

  const addWMSFiberSitesLayer = async (geoJsonFromApiData) => {
    try {
      if (map.current.getLayer('fiberSitesLayer')) {
        map.current.removeLayer('fiberSitesLayer');
      }
      if (map.current.getLayer('highlightedSites')) {
        map.current.removeLayer('highlightedSites');
      }
      if (map.current.getSource('fiberSitesLayer')) {
        map.current.removeSource('fiberSitesLayer');
      }

      // Remove old images
      if (map.current.hasImage('custom-tower2-onair')) {
        map.current.removeImage('custom-tower2-onair');
      }
      if (map.current.hasImage('custom-tower2-inprogress')) {
        map.current.removeImage('custom-tower2-inprogress');
      }
      if (map.current.hasImage('custom-tower2')) {
        map.current.removeImage('custom-tower2');
      }

      if (window.haloAnimationFrame) {
        cancelAnimationFrame(window.haloAnimationFrame);
      }

      map.current.addSource('fiberSitesLayer', {
        type: 'geojson',
        data: geoJsonFromApiData,
      });

      const createSvgIcon = (color) => {
        // Generate unique mask ID to avoid conflicts
        const maskId = `mask_${Math.random().toString(36).substr(2, 9)}`;
        return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <mask id="${maskId}" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
          <path d="M0 1.90735e-06H24V24H0V1.90735e-06Z" fill="white"/>
        </mask>
        <g mask="url(#${maskId})">
          <path d="M5.3916 23.2915H18.6104" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11.999 6.83454L12.2344 6.90396L16.0791 23.2915H7.91895L11.7778 6.84382L11.999 6.83454Z" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11.999 6.83447C12.8262 6.83447 13.501 6.15975 13.501 5.3325C13.501 4.5053 12.8262 3.83058 11.999 3.83058C11.1718 3.83058 10.4971 4.5053 10.4971 5.3325C10.4971 6.15975 11.1718 6.83447 11.999 6.83447Z" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.56957 7.91162C8.0104 7.16809 7.70801 6.26293 7.70801 5.33256C7.70801 4.34097 8.05141 3.38003 8.67987 2.61301" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6.50538 9.79883C5.4792 8.53653 4.91895 6.95938 4.91895 5.33258C4.91895 3.63514 5.52879 1.99414 6.63743 0.708688" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M15.4304 7.91162C15.9896 7.16809 16.292 6.26293 16.292 5.33256C16.292 4.34097 15.9485 3.38003 15.3201 2.61301" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M17.4948 9.79883C18.521 8.53653 19.0813 6.95938 19.0813 5.33258C19.0813 3.63514 18.4714 1.99414 17.3628 0.708688" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.12817 22.4001L14.9266 18.3787" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.79443 15.324L14.9073 18.3723" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.79443 15.324L13.6762 13.0494" stroke="${color}" stroke-width="1.40625" stroke-miterlimit="22.926" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      </svg>
    `;
      };

      // Create icons with different colors based on status
      // Green for On-Air, Red/Orange for In-Progress
      const onAirSvg = createSvgIcon('#33A400'); // Green for On-Air
      const inProgressSvg = createSvgIcon('#bd1d23'); // Orange for In-Progress
      const defaultSvg = createSvgIcon('#bd1d23'); // Default red

      const onAirSvgDataURL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(onAirSvg)}`;
      const inProgressSvgDataURL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(inProgressSvg)}`;
      const defaultSvgDataURL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(defaultSvg)}`;

      // Load all three images
      const onAirImg = new Image();
      const inProgressImg = new Image();
      const defaultImg = new Image();

      let imagesLoaded = 0;
      const totalImages = 3;

      const checkAndAddLayer = () => {
        imagesLoaded++;
        if (imagesLoaded === totalImages) {
          // All images loaded, now add the layer with data-driven icon selection
          map.current.addLayer({
            id: 'fiberSitesLayer',
            type: 'symbol',
            source: 'fiberSitesLayer',
            layout: {
              'icon-image': [
                'case',
                ['==', ['get', 'status'], 'On-Air'],
                'custom-tower2-onair',
                ['==', ['get', 'status'], 'In-Progress'],
                'custom-tower2-inprogress',
                'custom-tower2', // Default fallback
              ],
              'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 1, 13, 2],
              'icon-allow-overlap': true,
            },
          });
        }
      };

      onAirImg.onload = () => {
        if (!map.current.hasImage('custom-tower2-onair')) {
          map.current.addImage('custom-tower2-onair', onAirImg, { pixelRatio: 2 });
        }
        checkAndAddLayer();
      };
      onAirImg.onerror = () => {
        console.error('Failed to load On-Air icon');
        checkAndAddLayer();
      };
      onAirImg.src = `${onAirSvgDataURL}#${new Date().getTime()}-onair`;

      inProgressImg.onload = () => {
        if (!map.current.hasImage('custom-tower2-inprogress')) {
          map.current.addImage('custom-tower2-inprogress', inProgressImg, { pixelRatio: 2 });
        }
        checkAndAddLayer();
      };
      inProgressImg.onerror = () => {
        console.error('Failed to load In-Progress icon');
        checkAndAddLayer();
      };
      inProgressImg.src = `${inProgressSvgDataURL}#${new Date().getTime()}-inprogress`;

      defaultImg.onload = () => {
        if (!map.current.hasImage('custom-tower2')) {
          map.current.addImage('custom-tower2', defaultImg, { pixelRatio: 2 });
        }
        checkAndAddLayer();
      };
      defaultImg.onerror = () => {
        console.error('Failed to load default icon');
        checkAndAddLayer();
      };
      defaultImg.src = `${defaultSvgDataURL}#${new Date().getTime()}-default`;

      // Animation code remains the same
      let haloSize = 3;
      let opacity = 0.5;
      let expanding = true;
      let maxHaloSize = 6;
      let minHaloSize = 3;
      let maxOpacity = 0.7;
      let minOpacity = 0.3;

      const animateHalo = () => {
        if (expanding) {
          haloSize += 0.05;
          opacity += 0.02;
          if (haloSize >= maxHaloSize || opacity >= maxOpacity) {
            expanding = false;
          }
        } else {
          haloSize -= 0.05;
          opacity -= 0.02;
          if (haloSize <= minHaloSize || opacity <= minOpacity) {
            expanding = true;
          }
        }

        if (map.current.getLayer('highlightedSites')) {
          map.current.setPaintProperty('highlightedSites', 'circle-radius', haloSize);
          map.current.setPaintProperty('highlightedSites', 'circle-opacity', opacity);
        }

        requestAnimationFrame(animateHalo);
      };

      animateHalo();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error adding WFS layer:', error);
      }
    }
  };

  const removeLayer = () => {
    const sourceId = 'networklines';
    const layerId = 'lines-layer-network';

    if (map.current.getLayer(layerId)) {
      map.current.removeLayer(layerId);
    }
    if (map.current.getSource(sourceId)) {
      map.current.removeSource(sourceId);
    }
  };

  const handleClear = () => {
    setIDs([]);
    handleRefresh(map, markerRef);
  };

  const cancelRef = useRef(false);

  // useEffect(() => {
  //   cancelRef.current = false;

  //   const handleLayers = async () => {
  //     // showBackdrop();

  //     if (!map.current || !fiberSiteData.length || !showFiberCables) {
  //       return;
  //     }
  //     if (controllerRef.current) {
  //       controllerRef.current.abort();
  //     }
  //     controllerRef.current = new AbortController();
  //     addWmsLayer(
  //       map,
  //       currentKmzNetwork,
  //       currentPopup,
  //       stepRef,
  //       showBackdrop,
  //       hideBackdrop,
  //       controllerRef.current,
  //       fiberSiteData,
  //       showColorCoding
  //     );
  //     // hideBackdrop();

  //     if (cancelRef.current) {
  //       removeWmsLayer(map);
  //       removeWmsSitesLayer(map);
  //     }
  //   };

  //   handleLayers();

  //   return () => {
  //     cancelRef.current = true;
  //     if (controllerRef.current) {
  //       controllerRef.current.abort();
  //     }
  //   };
  // }, [currentKmzNetwork, fiberSiteData, showFiberCables]);

  useEffect(() => {
    handleClear();
  }, [currentKmzNetwork]);

  const handleFiberFilterChange = (field, value) => {
    setFiberFilters((prev) => {
      const newFilters = { ...prev, [field]: value };

      const order = ['region', 'city', 'nodeName', 'fiberPop', 'status', 'vendor'];
      const fieldIndex = order.indexOf(field);
      for (let i = fieldIndex + 1; i < order.length; i++) {
        newFilters[order[i]] = '';
      }

      return newFilters;
    });
  };

  const handleNodeFilterChange = (field, value) => {
    setNodeFilters((prev) => {
      const newFilters = { ...prev, [field]: value };

      const order = ['siteCode', 'technology', 'siteStatus'];
      const fieldIndex = order.indexOf(field);

      for (let i = fieldIndex + 1; i < order.length; i++) {
        newFilters[order[i]] = '';
      }

      const filtered = networkLayerData.filter((item) => {
        return (
          (!newFilters.siteCode || item.site_code === newFilters.siteCode) &&
          (!newFilters.technology || item.physical === newFilters.technology) &&
          (!newFilters.siteStatus || item.site_status === newFilters.siteStatus)
        );
      });

      setSiteCodeList([...new Set(filtered.map((item) => item.site_code).filter(Boolean))]);
      setTechnologiesList([...new Set(filtered.map((item) => item.physical).filter(Boolean))]);
      setSiteStatusList([...new Set(filtered.map((item) => item.site_status).filter(Boolean))]);

      return newFilters;
    });
  };

  const updateNodeDropdownOptions = () => {
    const unique = (key, data) => [...new Set(data.map((item) => item[key]).filter(Boolean))];

    const filteredBySiteCode = networkLayerData.filter(
      (item) => !nodeFilters.siteCode || item.site_code === nodeFilters.siteCode
    );

    const filteredByTechnology = filteredBySiteCode.filter(
      (item) => !nodeFilters.technology || item.physical === nodeFilters.technology
    );

    const filteredByStatus = filteredByTechnology.filter(
      (item) => !nodeFilters.siteStatus || item.site_status === nodeFilters.siteStatus
    );

    setSiteCodeList(unique('site_code', networkLayerData));
    setTechnologiesList(unique('physical', filteredBySiteCode));
    setSiteStatusList(unique('site_status', filteredByTechnology));
  };

  useEffect(() => {
    updateNodeDropdownOptions();
  }, [nodeFilters, networkLayerData]);

  const updateFiberDropdownOptions = () => {
    const unique = (key, data) => [...new Set(data.map((item) => item[key]).filter(Boolean))];

    const regionFilteredData = fiberSiteData.filter(
      (item) => !fiberFilters.region || item.region === fiberFilters.region
    );

    const cityFilteredData = regionFilteredData.filter(
      (item) => !fiberFilters.city || item.city === fiberFilters.city
    );

    const nodeFilteredData = cityFilteredData.filter(
      (item) => !fiberFilters.nodeName || item.site_id === fiberFilters.nodeName
    );

    const fiberPopFilteredData = nodeFilteredData.filter(
      (item) => !fiberFilters.fiberPop || item.fiber_pop_id === fiberFilters.fiberPop
    );

    const statusFilteredData = fiberPopFilteredData.filter(
      (item) => !fiberFilters.status || item.status === fiberFilters.status
    );

    const vendorFilteredData = statusFilteredData.filter(
      (item) => !fiberFilters.vendor || item.bp === fiberFilters.vendor
    );

    setCityList(unique('city', regionFilteredData));
    setVendorList(unique('bp', cityFilteredData));
    setNodeNameList(unique('site_id', vendorFilteredData));
    setFiberPopList(unique('fiber_pop_id', nodeFilteredData));
    setStatusList(unique('status', fiberPopFilteredData));
  };

  useEffect(() => {
    updateFiberDropdownOptions();
  }, [fiberFilters, fiberSiteData]);

  const getFilteredGeoJson = () => {
    const { siteCode, technology, siteStatus } = nodeFilters;
    const filtered = networkLayerData.filter((item) => {
      return (
        (!siteCode || item.site_code === siteCode) &&
        (!technology || item.physical === technology) &&
        (!siteStatus || item.site_status === siteStatus)
      );
    });

    return {
      type: 'FeatureCollection',
      features: filtered
        .filter((item) => item.lat && item.long)
        .map((item) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(item.long), parseFloat(item.lat)],
          },
          properties: {
            site_code: item.site_code,
            vendor: item.vendor,
            status: item.status,
            technology: item.physical,
            site_status: item.site_status,
            vendor_color: vendorColors[item.vendor] || vendorColors.default,
          },
        })),
    };
  };

  const getFiberSiteFilteredGeoJson = () => {
    const { nodeName, city, vendor, fiberPop, status, region } = fiberFilters;

    const filtered = fiberSiteData.filter((item) => {
      return (
        (!region || item.region === region) &&
        (!nodeName || item.site_id === nodeName) &&
        (!city || item.city === city) &&
        (!vendor || item.bp === vendor) &&
        (!fiberPop || item.fiber_pop_id === fiberPop) &&
        (!status || item.status === status)
      );
    });

    return {
      type: 'FeatureCollection',
      features: filtered
        .filter((item) => item.lat && item.long)
        .map((item) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(item.long), parseFloat(item.lat)],
          },
          properties: {
            region: item.region,
            vendor: item.vendor,
            status: item.status,
            fiberPop: item.fiber_pop,
            vendor_color: vendorColors[item.vendor] || vendorColors.default,
          },
        })),
    };
  };

  useEffect(() => {
    if (networkLayerData.length && map.current && showRanSites) {
      const filteredGeoJson = getFilteredGeoJson();
      addWmsSitessLayer(filteredGeoJson);
    }
  }, [nodeFilters]);

  useEffect(() => {
    if (fiberSiteData.length && showFiberSites) {
      addWMSFiberSitesLayer(getFiberSiteFilteredGeoJson());
    }
  }, [fiberFilters]);

  const handleFiberRefresh = () => {
    setFiberFilters({
      region: '',
      nodeName: '',
      city: '',
      vendor: '',
      fiberPop: '',
      status: '',
    });

    // Links layer is automatically created when linksData is available
    // Just ensure visibility is set correctly
    if (map.current && map.current.getLayer('linksLayers')) {
      map.current.setLayoutProperty(
        'linksLayers',
        'visibility',
        showFiberCables ? 'visible' : 'none'
      );
    }
    if (map.current && map.current.getLayer('linksLayers-dashed')) {
      map.current.setLayoutProperty(
        'linksLayers-dashed',
        'visibility',
        showFiberCables ? 'visible' : 'none'
      );
    }

    handleRefresh(map, markerRef);
  };

  const handleNodeRefresh = () => {
    setNodeFilters({
      siteCode: '',
      technology: '',
      siteStatus: '',
    });

    // if (showRanSites) {
    //   addWmsSitessLayer(getFilteredGeoJson());
    // }

    handleRefresh(map, markerRef);
  };

  useEffect(() => {
    if (!map.current) {
      return;
    }

    const { region, city, nodeName, vendor, fiberPop, status } = fiberFilters;

    const filteredSites = getFilteredFiberSites();

    if (region && !city) {
      const selectedRegion = regions.find(
        (r) =>
          r.regionName.replace(/\s/g, '').toLowerCase() === region.replace(/\s/g, '').toLowerCase()
      );
      if (selectedRegion?.center) {
        let zoomLevel = 6.5;

        flyToLocation(map.current, selectedRegion.center, zoomLevel);
        return;
      }
    }

    if (city) {
      const citySites = filteredSites.filter((site) => site.city === city);
      if (citySites.length > 0) {
        const first = citySites[0];
        const lng = parseFloat(first.long);
        const lat = parseFloat(first.lat);
        // Only fly to location if coordinates are valid
        if (!isNaN(lng) && !isNaN(lat) && isFinite(lng) && isFinite(lat)) {
          flyToLocation(map.current, [lng, lat], 8);
        }
        return;
      }
    }

    if (nodeName || fiberPop || vendor) {
      if (filteredSites.length > 0) {
        const first = filteredSites[0];
        const lng = parseFloat(first.long);
        const lat = parseFloat(first.lat);
        // Only fly to location if coordinates are valid
        if (!isNaN(lng) && !isNaN(lat) && isFinite(lng) && isFinite(lat)) {
          flyToLocation(map.current, [lng, lat], 12);
        }
        return;
      }
    }

    const noFiltersSelected = !region && !city && !nodeName && !vendor && !fiberPop && !status;

    if (noFiltersSelected) {
      map.current.flyTo({
        center: [lng, lat],
        zoom: 5,
        essential: true,
      });
    }
  }, [fiberFilters, fiberSiteData]);

  useEffect(() => {
    if (!map.current || !networkLayerData.length || !showRanSites) {
      return;
    }

    if (nodeFilters.siteCode || nodeFilters.technology || nodeFilters.siteStatus) {
      const filtered = getFilteredRanSites();
      if (filtered.length > 0) {
        const firstSite = filtered[0];
        const lng = parseFloat(firstSite.long);
        const lat = parseFloat(firstSite.lat);
        // Only fly to location if coordinates are valid
        if (!isNaN(lng) && !isNaN(lat) && isFinite(lng) && isFinite(lat)) {
          flyToLocation(map.current, [lng, lat], 10);
        }
      }
    }

    const noFiltersSelected =
      !nodeFilters.siteCode && !nodeFilters.technology && !nodeFilters.siteStatus;

    if (noFiltersSelected) {
      map.current.flyTo({
        center: [lng, lat],
        zoom: 5,
        essential: true,
      });
    }
  }, [nodeFilters]);

  return (
    <>
      {!leftPanelOpen && (
        <>
          <Grid container>
            <Grid item xs={0.5}>
              {' '}
            </Grid>
            <Grid item xs={11.5}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  // left: '78px',
                  left: '4%',
                  zIndex: 999,
                  cursor: 'pointer',
                }}
                onClick={() => setLeftPanelOpen(true)}
              >
                <img alt="" src={expandButton} style={{ width: '43px', height: '152px' }} />
              </Box>
            </Grid>
          </Grid>
        </>
      )}
      <Box
        sx={{
          height: 'calc(100vh - 72px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', flex: 1, position: 'relative', width: '100%', minHeight: 0 }}>
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: leftPanelOpen ? '370px' : '0px',
              width: leftPanelOpen ? 'calc(100% - 380px)' : '100%',
              display: 'flex',
              gap: '10px',
              zIndex: 1,
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <div
              style={{
                display: viewType === 'table' ? 'block' : 'none',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    padding: 8,
                    background: '#f7f7f7',
                    boxShadow: '0px 0px 30px 2px rgba(0, 0, 0, 0.05)',
                    borderRadius: 8,
                    height: 30,
                    width: 30,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <ArrowBackIosNewIcon style={{ fontSize: 18, color: '#1C274C' }} />
                </div>
                <div style={{ color: 'black', fontSize: 18, fontWeight: '500' }}>
                  Network Layer Visualization
                </div>
              </div>
            </div>
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                right: 20,
                zIndex: 10,
                display: 'flex',
                gap: '10px',
              }}
            >
              <Tooltip title="See Filters">
                <Box
                  className="table-tab-button2"
                  ref={seeButtonRef}
                  onClick={() => setPopupOpen((prev) => !prev)}
                >
                  <Typography>Select</Typography>
                  <RemoveRedEyeOutlinedIcon sx={{ fontSize: '20px' }} />
                </Box>
              </Tooltip>

              <Box className="map-table-group">
                <Tooltip title="Map">
                  <MapIcon
                    className={`map-table-btn ${viewType === 'graph' ? 'active' : ''}`}
                    onClick={() => setViewType('graph')}
                  />
                </Tooltip>
                <Tooltip title="Table">
                  <TableChartIcon
                    className={`map-table-btn ${viewType === 'table' ? 'active' : ''}`}
                    onClick={() => setViewType('table')}
                  />
                </Tooltip>
              </Box>
            </Box>

            {/* Vendor Color Legend */}
            {showColorCoding && viewType === 'graph' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: popupOpen ? `${legendTop}px` : '70px',
                  right: 20,
                  zIndex: popupOpen ? 1501 : 10, // Higher z-index when popup is open to ensure it's above
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  padding: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  minWidth: '200px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    color: '#192435',
                  }}
                >
                  Vendor Legend
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  {Object.entries(vendorColorMap).map(([vendor, color]) => (
                    <Box
                      key={vendor}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <Box
                        sx={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '2px',
                          backgroundColor: color,
                          border: '1px solid #e0e0e0',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: '#192435',
                          textTransform: 'capitalize',
                        }}
                      >
                        {vendor}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </div>

          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '380px',
              height: '100%',
              zIndex: 999,
              backgroundColor: 'white',
              boxShadow: '2px 0 8px rgba(0, 0, 0, 0.2)',
              transform: leftPanelOpen ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.3s ease-in-out',
            }}
          >
            {leftPanelOpen && (
              <button className="crossButton" onClick={() => setLeftPanelOpen((prev) => !prev)}>
                <img alt="cross icon" src={crossIcon} />
              </button>
            )}

            {/* Panel content */}
            <Box
              sx={{
                padding: '16px',
                height: '100%',
                boxSizing: 'border-box',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#F7F7F7',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  marginTop: '16px',
                }}
              >
                {/* Header */}
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>Fibers</Typography>
                  <Tooltip title="Refresh">
                    <RefreshIcon
                      style={{ cursor: 'pointer', fontSize: '20px', fontWeight: '400' }}
                      onClick={() => handleFiberRefresh()}
                    />
                  </Tooltip>
                </Box>

                {/* Four sections with checkboxes */}
                <Grid container spacing={1}>
                  {regionList.map((label, idx) => (
                    <Grid item xs={3} key={idx}>
                      <Box
                        sx={{
                          backgroundColor: '#EEEEEE',
                          borderRadius: '10px',
                          padding: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          className="customCheckbox"
                          style={{ alignSelf: 'flex-start' }}
                          checked={fiberFilters.region === label}
                          onChange={() =>
                            handleFiberFilterChange(
                              'region',
                              fiberFilters.region === label ? '' : label
                            )
                          }
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontStyle: 'Medium',
                            fontSize: '12px',
                            leadingTrim: 'NONE',
                            lineHeight: '20px',
                            letterSpacing: '0%',
                            // textAlign: 'center',
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {FiberDropdownConfig.map(({ key, label, options }, index) => (
                  <Box key={index}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 0.5, fontSize: '12px', color: '#656E7A' }}
                    >
                      {label}
                    </Typography>
                    <select
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '10px',
                        border: '1px solid #ccc',
                        backgroundColor: '#fff',
                        outline: 'none',
                      }}
                      value={fiberFilters[key]}
                      onChange={(e) => handleFiberFilterChange(key, e.target.value)}
                    >
                      <option value="">Select</option>
                      {options.map((option, idx) => (
                        <option key={idx} value={option} style={{ color: '#656E7A' }}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </Box>
                ))}

                {/* Checkbox below the select */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#bd1d23',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#bd1d23',
                          },
                        }}
                        checked={showColorCoding}
                        onChange={(e) => setShowColorCoding(e.target.checked)}
                        name="showColorCoding"
                      />
                    }
                    label="Show vendor based color coding"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        marginLeft: '4px',
                        fontSize: '13px',
                        color: '#192435',
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#F7F7F7',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  marginTop: '30px',
                }}
              >
                {/* Header */}
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>Nodes</Typography>
                  <Tooltip title="Refresh">
                    <RefreshIcon
                      style={{ cursor: 'pointer', fontSize: '20px', fontWeight: '400' }}
                      onClick={() => handleNodeRefresh()}
                    />
                  </Tooltip>
                </Box>

                {/* Dropdowns */}
                {NodeDropdownConfig.map(({ key, label, options }, index) => (
                  <Box key={index}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 0.5, fontSize: '12px', color: '#656E7A' }}
                    >
                      {label}
                    </Typography>
                    <select
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '10px',
                        border: '1px solid #ccc',
                        backgroundColor: '#fff',
                        outline: 'none',
                      }}
                      value={nodeFilters[key]}
                      onChange={(e) => handleNodeFilterChange(key, e.target.value)}
                    >
                      <option value="">Select</option>
                      {options.map((option, idx) => (
                        <option key={idx} value={option} style={{ color: '#656E7A' }}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              transition: 'margin-left 0.3s ease, width 0.3s ease',
              marginLeft: leftPanelOpen ? '380px' : 0,
              width: leftPanelOpen ? 'calc(100% - 380px)' : '100%',
              position: 'relative',
            }}
          >
            {/* Map View */}
            <div
              style={{
                display: viewType === 'graph' ? 'block' : 'none',
                width: '100%',
                height: '100%',
              }}
            >
              <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
              <RefreshMap map={map} markerRef={markerRef} />
            </div>

            {/* Table View */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                left: 0,
                top: 0,
                display: viewType === 'table' ? 'block' : 'none',
                width: '100%',
                height: '100%',
                overflow: 'auto',
                backgroundColor: '#fff',
              }}
            >
              <div style={{ padding: '10px', marginLeft: leftPanelOpen ? '0px' : '30px' }}>
                <NetworkLayerTable
                  ranData={showRanSites ? getFilteredRanSites() : []}
                  fiberData={showFiberSites ? getFilteredFiberSites() : []}
                  inventoryData={inventoryData}
                />
              </div>
            </div>
          </Box>
        </Box>
      </Box>

      <ViewPopup
        anchorEl={seeButtonRef.current}
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        onToggleRan={handleRanToggle}
        onToggleFiber={handleFiberToggle}
        onToggleCables={handleFiberCableToggle}
        showRan={showRanSites}
        showFiber={showFiberSites}
        showFiberCables={showFiberCables}
      />
    </>
  );
};

export default NetworkLayerMapComponent;
